import _ from 'lodash';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { useContext } from 'react';
import styled from 'styled-components';
import { Trans, useTranslation } from 'react-i18next';

import { ACTOR_PHRASES } from 'components/customer/conversation_history/conversation_items_v2/metadata/actor_sentence';
import BubbleContent from 'components/customer/conversation_history/conversation_items_v2/content/bubble_content';
import Communicator from 'models/communicator';
import ConversationItem from 'models/conversation_item';
import ConversationMessageAttachments from 'components/customer/conversation_history/conversation_items_v2/message/attachments/conversation_message_attachments';
import { getMessagingAttachmentUrl } from 'scripts/domain/services/attachments';
import InlineAnnotatedContentContainer from 'components/lib/inline_annotated_content_container';
import {
  InstagramCommentWithImage,
  InstagramCommentWithVideo,
  ModerationStatusIndicator,
} from 'components/customer/conversation_history/conversation_items_v2/message/instagram_comment';
import InstagramMetadataIcon from 'components/customer/conversation_history/conversation_items_v2/message/instagram_metadata_icon';
import ItemCommError from 'components/customer/conversation_history/conversation_items_v2/item_comm_error';
import ItemContent from 'components/customer/conversation_history/conversation_items_v2/item_content';
import ItemContext from 'components/customer/conversation_history/conversation_items_v2/item_context';
import ItemMetadata from 'components/customer/conversation_history/conversation_items_v2/item_metadata';
import LinkOutStrokeIcon from 'components/common/icons/stroke/link-out-stroke';
import { MessageChannelType, MessageStatus } from 'models/conversation_message';
import {
  MessageItemImage,
  MessageItemVideo,
} from 'components/customer/conversation_history/conversation_items_v2/message/message_item_asset';
import MessageReaction from 'components/customer/conversation_history/conversation_items_v2/message/message_reaction';
import SwitchCurrentConversation from 'actions/customer/switch_current_conversation';
import TwitterMetadataIcon from 'components/customer/conversation_history/conversation_items_v2/message/twitter_metadata_icon';
import useConditionalTranslation from 'components/hooks/use_conditional_translation';
import { useExecuteAction } from 'components/hooks/connect_hooks';
import WhatsAppMetadataIcon from 'components/customer/conversation_history/conversation_items_v2/message/whatsapp_metadata_icon';

export default function MessageItem({
  friendlyCompanyAddress,
  friendlyCustomerAddress,
  hasReaction,
  isFirstInStack,
  isLastInStack,
  isStacked,
  item,
  orgId,
}) {
  const { t } = useTranslation();
  const executeAction = useExecuteAction();
  const channelType = item.content.getChannelType();
  return (
    <ItemContent
      isFirstInStack={isFirstInStack}
      isLastInStack={isLastInStack}
      isStacked={isStacked}
      item={item}
      showIcon={!isStacked}
    >
      <ItemMetadata
        channel={getRepliedViaChannel(item)}
        icon={getIcon(channelType)}
        item={item}
        phrase={getAgentPhrase(item)}
        phraseComponents={getAgentPhraseComponents(item)}
        showMetadata={!isStacked}
        status={getStatus(item)}
        text={getItemMetadata(item)}
        tooltip={getTooltipText(item)}
      />
      <MessageContent hasReaction={hasReaction} isStacked={isStacked} item={item} orgId={orgId} />
      <ItemCommError item={item} />
    </ItemContent>
  );

  function getIcon(channel) {
    // Add future messaging channel icons here
    switch (channel) {
      case MessageChannelType.TWITTER:
        return <TwitterMetadataIcon />;
      case MessageChannelType.WHATSAPP:
        return <WhatsAppMetadataIcon />;
      case MessageChannelType.INSTAGRAM_COMMENT:
      case MessageChannelType.INSTAGRAM_DIRECT:
        return <InstagramMetadataIcon />;
      default:
        return null;
    }
  }

  function getStatus(messageItem) {
    let status = messageItem.content.getStatus();
    switch (status) {
      case MessageStatus.DELIVERED:
      case MessageStatus.SENT:
        return 'delivered';
      case MessageStatus.FAILED:
      case MessageStatus.UNDELIVERED:
      case MessageStatus.UNKNOWN:
        return 'delivery failed';
      case MessageStatus.NEW:
      case MessageStatus.SENDING:
        return 'sending';
      case MessageStatus.RECEIVED:
        return 'received';
      case MessageStatus.READ:
        return 'read';
      default:
        return undefined;
    }
  }

  // Every agent-authored line delegates its sentence to AgentMetadata so the
  // verb agrees with the actor. Plain channels use the default "replied via
  // <channel>" phrase and hand over the channel name; Instagram comments name
  // their own phrase, in two forms depending on whether the original comment can
  // be linked to.
  function getRepliedViaChannel(messageItem) {
    if (!isAgentComment(messageItem) && messageItem.initiator.type === Communicator.AGENT) {
      return messageItem.content.getChannelName();
    }
    return undefined;
  }

  function getAgentPhrase(messageItem) {
    if (!isAgentComment(messageItem)) {
      return undefined;
    }
    return item.relatedItemId?.conversationItemId
      ? ACTOR_PHRASES.REPLIED_TO_INSTAGRAM_COMMENT_LINKED
      : ACTOR_PHRASES.REPLIED_TO_INSTAGRAM_COMMENT;
  }

  function getAgentPhraseComponents(messageItem) {
    if (!isAgentComment(messageItem) || !item.relatedItemId?.conversationItemId) {
      return undefined;
    }
    return {
      commentLink: (
        <OriginalCommentItemLink
          className="conversationItem-metadata-link"
          onClick={() =>
            executeAction(SwitchCurrentConversation, {
              conversationId: item.relatedItemId.conversationId,
              conversationItemId: item.relatedItemId.conversationItemId,
            })
          }
        />
      ),
    };
  }

  function isAgentComment(messageItem) {
    return (
      messageItem.initiator.type === Communicator.AGENT &&
      messageItem.content.channel === MessageChannelType.INSTAGRAM_COMMENT
    );
  }

  function getItemMetadata(messageItem) {
    switch (messageItem.initiator.type) {
      case Communicator.AGENT:
        // Sentence omitted on purpose: AgentMetadata builds it from the phrase
        // and channel handed over above, so the verb can agree with the actor
        // ("Respondiste por WhatsApp", not "Tú respondió por WhatsApp").
        return undefined;
      case Communicator.CUSTOMER:
        switch (messageItem.content.channel) {
          case MessageChannelType.INSTAGRAM_COMMENT:
            return (
              <span>
                <Trans
                  components={{
                    postLink: (
                      <InstagramCommentPostLink
                        href={messageItem.content.getMediaPermalink()}
                        rel="noopener noreferrer"
                        target="_blank"
                      />
                    ),
                    linkOutIcon: <LinkOutStrokeIcon />,
                  }}
                  i18nKey="commented on {{companyAddress}}'s <postLink>post <linkOutIcon></linkOutIcon></postLink>"
                  values={{ companyAddress: friendlyCompanyAddress }}
                />
              </span>
            );
          case MessageChannelType.INSTAGRAM_DIRECT:
            if (messageItem.content.getStoryMentionUrl()) {
              return t('mentioned {{companyAddress}} via Instagram Story', {
                companyAddress: friendlyCompanyAddress,
              });
            }
            if (messageItem.content.getStoryReplyUrl()) {
              return t('replied to {{companyAddress}} Story', { companyAddress: friendlyCompanyAddress });
            }
            return t('sent an Instagram Message');
          default:
            return t('sent a {{channel}}', { channel: messageItem.content.getChannelName() });
        }
      default:
        return null;
    }
  }

  function getTooltipText() {
    if (!friendlyCustomerAddress || !friendlyCompanyAddress) {
      return null;
    }

    switch (item.initiator.type) {
      case Communicator.AGENT:
        return t('sent from {{from}} to {{to}}', { from: friendlyCompanyAddress, to: friendlyCustomerAddress });
      case Communicator.CUSTOMER:
        return t('sent from {{from}} to {{to}}', { from: friendlyCustomerAddress, to: friendlyCompanyAddress });
      default:
        return null;
    }
  }
}

MessageItem.propTypes = {
  friendlyCompanyAddress: PropTypes.string,
  friendlyCustomerAddress: PropTypes.string,
  hasReaction: PropTypes.bool,
  isFirstInStack: PropTypes.bool,
  isLastInStack: PropTypes.bool,
  isStacked: PropTypes.bool,
  item: PropTypes.instanceOf(ConversationItem).isRequired,
  orgId: PropTypes.string.isRequired,
};

export function MessageContent({ item, hasReaction, isStacked, orgId }) {
  const { t } = useTranslation();
  const channelType = _.trim(item.content.getChannelType());
  const isAutoreply = item.initiator.type === Communicator.AUTOMATED;
  const isInstagramDirectMessage = channelType === MessageChannelType.INSTAGRAM_DIRECT;
  const isInstagramComment = channelType === MessageChannelType.INSTAGRAM_COMMENT;

  if (item.content.isVideoMessage()) {
    return renderVideo();
  }

  if (isInstagramDirectMessage) {
    if (item.content.isStoryImageMessage()) {
      return renderStoryImage();
    }
    if (item.content.isStoryVideoMessage()) {
      return renderVideo();
    }
  }

  if (isInstagramComment) {
    return renderInstagramCommentWithPreview();
  }

  return (
    <BubbleContent
      className={classnames('messageItem-content', _.camelCase(channelType))}
      data-aid={`messageItem-${item?.id}`}
      hasReaction={hasReaction}
      item={item}
    >
      {isAutoreply ? <div className="messageItem-autoreply">{t('Autoreply')}</div> : null}
      <MessageText item={item} />
      {renderAttachments()}
      {renderReaction()}
    </BubbleContent>
  );

  function renderAttachments() {
    let itemAttachments = item.content.getAttachments();
    if (itemAttachments.length === 0) {
      return null;
    }
    return <ConversationMessageAttachments attachments={itemAttachments} />;
  }

  function renderReaction() {
    if (item.content.reactions && item.content.reactions.length > 0) {
      return <MessageReaction timestamp={item.content.reactions[0].timestamp} />;
    }
    return null;
  }

  function renderVideo() {
    const videos = item.content.getVideoAttachments();
    let url;
    let isAvailable = false;
    switch (item.content.channel) {
      case MessageChannelType.INSTAGRAM_DIRECT:
        // For instagram we load the video from the URL provided by Meta so we don't need to check if it's available.
        isAvailable = true;
        url =
          (videos.length === 1 && videos[0].source.path) ||
          item.content.getStoryMentionUrl() ||
          item.content.getStoryReplyUrl();
        break;

      case MessageChannelType.WHATSAPP:
        isAvailable = videos[0].isAvailable();
        url = getMessagingAttachmentUrl(orgId, videos[0]);
        break;

      default:
        return null;
    }

    if (!url) {
      return null;
    }
    return <MessageItemVideo isAvailable={isAvailable} item={item} url={url} />;
  }

  function renderStoryImage() {
    const url = item.content.getStoryMentionUrl() || item.content.getStoryReplyUrl();
    return <MessageItemImage item={item} url={url} />;
  }

  function renderInstagramCommentWithPreview() {
    const mediaUrl = item.content.getMediaUrl();
    const mediaType = item.content.getMediaType();

    if (mediaType) {
      // Determine if we should use video component
      const shouldDisplayUsingVideoComponent =
        mediaUrl &&
        (mediaType === 'VIDEO' ||
          (mediaType === 'CAROUSEL_ALBUM' &&
            (mediaUrl.includes('.mp4') || mediaUrl.includes('.mov') || mediaUrl.includes('.avi'))));

      return shouldDisplayUsingVideoComponent ? (
        <InstagramCommentWithVideo isStacked={isStacked} item={item} mediaUrl={mediaUrl} />
      ) : (
        <InstagramCommentWithImage isStacked={isStacked} item={item} mediaUrl={mediaUrl} />
      );
    }

    // Fallback to regular message if no media preview available
    return (
      <BubbleContent
        className={classnames('messageItem-content', _.camelCase(channelType))}
        data-aid={`messageItem-${item?.id}`}
        hasReaction={hasReaction}
        item={item}
      >
        {isAutoreply ? <div className="messageItem-autoreply">{t('Autoreply')}</div> : null}
        <MessageText item={item} />
        {renderAttachments()}
        {renderReaction()}
        <ModerationStatusIndicator item={item} />
      </BubbleContent>
    );
  }
}

MessageContent.propTypes = {
  hasReaction: PropTypes.bool,
  isStacked: PropTypes.bool,
  item: PropTypes.instanceOf(ConversationItem).isRequired,
  orgId: PropTypes.string.isRequired,
};

export function MessageText({ item }) {
  const { isViewTranslation } = useContext(ItemContext);
  const text = useConditionalTranslation(item, !isViewTranslation);

  return (
    <div dir="auto">
      <InlineAnnotatedContentContainer
        hasRedactedPaymentCard={item.content.hasRedactedPaymentCardNumber()}
        id={item.id}
        isRedacted={item.content.isRedacted}
        text={text}
      />
    </div>
  );
}

MessageText.propTypes = {
  item: PropTypes.instanceOf(ConversationItem).isRequired,
};

const InstagramCommentPostLink = styled.a`
  color: ${p => p.theme.colors.green400};
  font-weight: bold;
  text-decoration: none;
  display: inline-flex;
  align-items: center;

  svg {
    width: 12px;
    height: 12px;
    fill: ${p => p.theme.colors.green400};
  }
`;

const OriginalCommentItemLink = styled.a`
  color: ${p => p.theme.colors.green400};
  font-weight: bold;
`;
